import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { STORAGE_KEYS } from '@/constants/storageKeys';

const useSelectedFacilitiesStorageByCompanyId = (companyId) => {
    const userId = useSelector((state) => state.Auth?.userId);
    const storageKey = STORAGE_KEYS.portfolioSelectedFacilities(userId);

    const getStorageData = useCallback(() => {
        try {
            if (!userId) return {};
            const stored = localStorage.getItem(storageKey);
            return stored ? JSON.parse(stored) : {};
        } catch (error) {
            console.error('Failed to parse localStorage data:', error);
            return {};
        }
    }, [storageKey, userId]);

    const saveStorageData = useCallback((data) => {
        try {
            if (!userId) return;
            localStorage.setItem(storageKey, JSON.stringify(data));
        } catch (error) {
            console.error('Failed to save to localStorage:', error);
        }
    }, [storageKey, userId]);

    const [selectedFacilities, setSelectedFacilitiesState] = useState(() => {
        if (!companyId || !userId) return new Set();

        const storageData = getStorageData();
        const facilityIds = storageData[companyId] || [];
        return new Set(facilityIds);
    });

    useEffect(() => {
        if (!companyId || !userId) {
            setSelectedFacilitiesState(new Set());
            return;
        }

        const storageData = getStorageData();
        const facilityIds = storageData[companyId] || [];
        setSelectedFacilitiesState(new Set(facilityIds));
    }, [companyId, userId, getStorageData]);

    const setSelectedFacilities = useCallback((newSelectedFacilities) => {
        if (!companyId || !userId) return;

        const updatedSet = typeof newSelectedFacilities === 'function'
            ? newSelectedFacilities(selectedFacilities)
            : newSelectedFacilities;

        setSelectedFacilitiesState(updatedSet);

        const storageData = getStorageData();
        storageData[companyId] = Array.from(updatedSet);
        saveStorageData(storageData);
    }, [companyId, userId, selectedFacilities, getStorageData, saveStorageData]);

    const hasCache = useCallback((targetCompanyId) => {
        if (!targetCompanyId || !userId) return false;

        const storageData = getStorageData();
        return storageData[targetCompanyId] !== undefined;
    }, [getStorageData, userId]);

    return [selectedFacilities, setSelectedFacilities, hasCache];
};

export default useSelectedFacilitiesStorageByCompanyId;
